import React, { createContext, useMemo, useState } from 'react';
import {
  NavigationProps,
  GENERAL_MODULES,
  PORTFOLIO_COMPANY_MODULES_SECTION_1,
  REGULAR_COMPANY_MODULES_SECTION_1,
  REGULAR_COMPANY_MODULES_SECTION_2,
  REGULAR_COMPANY_MODULES_SECTION_3,
  GROUP_COMPANY_MODULES_SECTION_1,
  GROUP_COMPANY_MODULES_SECTION_2,
  AccessFlags,
} from '.';
import { useCompanyType, CompanyType, useCurrentCompany } from 'utils/hooks';
import { VStack, HStack, StackProps, Stack } from '@chakra-ui/react';
import { SideMenu } from './pieces';
import { useFeatureFlags, useUserActions, useUserSetting } from './Navigation.hooks';
import { HelpMenu } from 'Organisms/HelpMenu';
import { DowntimeNotification } from 'Molecules/DowntimeNotification/DowntimeNotification';
import { format } from 'date-fns';
import { MaintenanceBanner } from 'Molecules/MaintenanceBanner';
import { Modal } from 'Molecules';
import { Typography } from 'Tokens';
import { useGetMaintainanceStatusSubscription } from 'models';

const LayoutWrapper = (props: StackProps) => (
  <VStack
    {...props}
    flexGrow="1"
    bg="bg.default"
    marginInlineStart="0px !important"
    marginStart="0px !important"
    gap="0px"
    overflow="auto"
  />
);
const PageWrapper = (props: StackProps) => (
  <HStack {...props} height="100vh" width="100vw" gap="0px" />
);

export const GlobalContext = createContext({
  walktourActive: false,
  showWalktourTooltip: false,
  helpCenterOpen: false,
  showInfoBoxes: false,
  setWalktourActive: (_val: boolean) => {},
  setHelpCenterOpen: (_val: boolean) => {},
  setShowInfoBoxes: (_val: boolean) => {},
  setShowWalktourTooltip: (_val: boolean) => {},
});

export const MAINTENANCE_DATES = {
  startTime: new Date('2025-01-21T12:00:00'),
  endTime: new Date('2025-09-28T22:00:00'),
};

export const Navigation = ({ children }: NavigationProps) => {
  const { languageEditor } = useUserActions();
  const { companyType, isInGroups } = useCompanyType();
  const { company } = useCurrentCompany();
  const [helpCenterOpen, setHelpCenterOpen] = useState(false);
  const [walktourActive, setWalktourActive] = useState(false);
  const [showInfoBoxes, setShowInfoBoxes] = useState(false);
  const [showWalktourTooltip, setShowWalktourTooltip] = useState(false);
  const [isMaintenanceModalOpen, setMaintenanceModalOpen] = useState(false);

  const formattedStartTime = format(MAINTENANCE_DATES.startTime, 'yyyy-MM-dd_HH-mm');
  const formattedEndTime = format(MAINTENANCE_DATES.endTime, 'yyyy-MM-dd_HH-mm');

  const [showDowntimeNotification, setShowDowntimeNotification] = useUserSetting(
    `show-downtime-notification-${formattedStartTime}-${formattedEndTime}`,
    true
  );

  const { data: appConfig } = useGetMaintainanceStatusSubscription();

  const isMergeInProgress = appConfig?.AppConfig_by_pk?.mergeInProgress;

  const {
    assessments: hasTaxonomyAccess,
    pai: hasPaiAccess,
    hasSustainabilityAccess,
  } = useFeatureFlags({ company });

  const sideMenuItems = useMemo(() => {
    if (companyType === CompanyType.regular) {
      return [
        ...REGULAR_COMPANY_MODULES_SECTION_1.filter((section) => {
          if (section.key === AccessFlags.SUSTAINABILITY && !hasSustainabilityAccess) {
            return false;
          }
          if (section.key === AccessFlags.PAI && !hasPaiAccess) {
            return false;
          }
          if (section.key === AccessFlags.TAXONOMY && !hasTaxonomyAccess) {
            return false;
          }
          if (section.key === 'taskManagement') {
            return hasSustainabilityAccess;
          }
          return true;
        }),
        ...REGULAR_COMPANY_MODULES_SECTION_2.filter((section) => {
          if (section.key === AccessFlags.PAI && !hasPaiAccess) {
            return false;
          }
          if (section.key === AccessFlags.TAXONOMY && !hasTaxonomyAccess) {
            return false;
          }
          if (section.key === AccessFlags.SUSTAINABILITY && !hasSustainabilityAccess) {
            return false;
          }
          if (section.key === 'groups') {
            return isInGroups;
          }
          return true;
        }),
        ...REGULAR_COMPANY_MODULES_SECTION_3,
        ...GENERAL_MODULES,
      ];
    } else if (companyType === CompanyType.portfolio) {
      return [
        ...PORTFOLIO_COMPANY_MODULES_SECTION_1,
        ...REGULAR_COMPANY_MODULES_SECTION_3,
        ...GENERAL_MODULES.filter((item) => item.key !== 'integrations' && item.key !== 'groups'),
      ];
    }
    return [
      ...GROUP_COMPANY_MODULES_SECTION_1.filter((section) => {
        if (section.key === AccessFlags.TAXONOMY && !hasTaxonomyAccess) {
          return false;
        }
        if (section.key === AccessFlags.SUSTAINABILITY && !hasSustainabilityAccess) {
          return false;
        }
        if (section.key === 'taskManagement') {
          return hasSustainabilityAccess;
        }
        return true;
      }),
      ...GROUP_COMPANY_MODULES_SECTION_2.filter((section) =>
        section.key === 'groups' ? isInGroups : true
      ),
      ...REGULAR_COMPANY_MODULES_SECTION_3,
      ...GENERAL_MODULES.filter((item) => item.key !== 'groups'),
    ];
  }, [companyType, isInGroups, company, hasTaxonomyAccess, hasPaiAccess, hasSustainabilityAccess]);

  const context = {
    walktourActive: walktourActive,
    setWalktourActive: setWalktourActive,
    helpCenterOpen: helpCenterOpen,
    setHelpCenterOpen: setHelpCenterOpen,
    showInfoBoxes: showInfoBoxes,
    setShowInfoBoxes: setShowInfoBoxes,
    showWalktourTooltip: showWalktourTooltip,
    setShowWalktourTooltip: setShowWalktourTooltip,
  };

  return (
    <VStack alignItems="stretch" gap="0px">
      {showDowntimeNotification && (
        <DowntimeNotification
          startTime={MAINTENANCE_DATES.startTime}
          endTime={MAINTENANCE_DATES.endTime}
          onClose={() => setShowDowntimeNotification(false)}
        />
      )}
      {isMergeInProgress && (
        <MaintenanceBanner setMaintenanceModalOpen={() => setMaintenanceModalOpen(true)} />
      )}
      <Modal
        size="md"
        isOpen={isMaintenanceModalOpen}
        title="System merge in progress"
        hasFooter={false}
        onClose={() => setMaintenanceModalOpen(false)}
        isCentered
      >
        <Typography variant="body">
          We are currently merging updates, which may cause temporary disruptions. You may encounter
          errors, and unsaved data could be lost. To prevent issues, please pause your work for a
          few minutes. The banner will disappear when the system is stable again.
        </Typography>
      </Modal>
      <PageWrapper alignItems="stretch">
        <GlobalContext.Provider value={context}>
          <SideMenu items={sideMenuItems} />
          <LayoutWrapper>
            <Stack
              mt="0px !important" // LayoutWrapper should be a box: https://github.com/chakra-ui/chakra-ui/issues/2578
              width="100%"
              overflowX="auto"
              flexGrow="1"
            >
              {children}
            </Stack>
            {languageEditor}
          </LayoutWrapper>
          <HelpMenu />
        </GlobalContext.Provider>
      </PageWrapper>
    </VStack>
  );
};
